/**
 * The platform's own sites that nobody watches yet, one click each to open
 * the add dialog with the site already picked (item K).
 *
 * The same idea as the client suggestions under it: the sites already exist
 * on the platform, and retyping their addresses is where typos come from. The
 * button opens the dialog rather than adding at once, because a watched site
 * needs a client and a package, and only a person knows both. A client whose
 * website is on the site's address is named on the row and picked in the
 * dialog for them.
 *
 * Only drawn for somebody who may add sites, and only while something is left
 * to add.
 */
import { Plus, Server } from "lucide-react";
import type { PlatformSiteOption } from "../../../../lib/monitoringPlatform";
import { Btn, CardHead, StatusPill } from "../../../ui";
import type { T } from "../words";

export function PlatformSitesCard({
  sites,
  t,
  onAdd,
}: {
  /** The sites nobody watches yet. */
  sites: readonly PlatformSiteOption[];
  t: T;
  onAdd: (site: PlatformSiteOption) => void;
}) {
  if (sites.length === 0) return null;
  return (
    <section className="crm-card mt-4">
      <CardHead
        title={
          <span className="inline-flex items-center gap-2">
            <Server className="size-4 text-[var(--color-primary)]" strokeWidth={1.8} />
            {t("monitoring.platform.suggest.title")}
          </span>
        }
        sub={t("monitoring.platform.suggest.sub", { n: sites.length })}
      />
      <ul className="flex flex-col px-[18px] pb-4">
        {sites.map((s) => (
          <li key={s.siteId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl px-2 py-2.5 hover:bg-[var(--crm-sunk)]">
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-2">
                <span className="crm-prow-n crm-trunc">{s.name}</span>
                <StatusPill tone={s.owner === "customer" ? "mut" : "info"}>{t(`monitoring.platform.owner.${s.owner}`)}</StatusPill>
              </span>
              <span className="crm-prow-s crm-trunc block">
                {s.client ? t("monitoring.platform.suggest.withClient", { host: s.host, name: s.client.name }) : s.host}
              </span>
            </span>
            <Btn small onClick={() => onAdd(s)}>
              <Plus className="size-[15px]" strokeWidth={1.8} />
              {t("monitoring.suggest.add")}
            </Btn>
          </li>
        ))}
      </ul>
    </section>
  );
}
